import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useUI } from '../../contexts/UIContext';
import { useUserProfile } from '../../hooks/useUserProfile';
import {
  UserCircleIcon,
  EnvelopeIcon,
  BuildingOfficeIcon,
  CalendarIcon,
  CheckIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const UserProfileSettingsImproved = () => {
  const { user } = useAuth();
  const { profile: userProfile, loading: profileLoading } = useUserProfile();
  const { showNotification } = useUI();
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState({
    first_name: '',
    last_name: '',
    email: '',
    organization: '',
    role: '',
    joined_date: ''
  });

  useEffect(() => {
    if (user && userProfile) {
      setProfile({
        first_name: userProfile.first_name || user.user_metadata?.first_name || '',
        last_name: userProfile.last_name || user.user_metadata?.last_name || '',
        email: user.email || '',
        organization: userProfile.organization_name || user.user_metadata?.organization_name || '',
        role: userProfile.role || user.user_metadata?.role || 'member',
        joined_date: user.created_at ? new Date(user.created_at).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        }) : ''
      });
    }
  }, [user, userProfile]);

  const handleInputChange = (field, value) => {
    setProfile(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const saveProfile = async () => {
    try {
      setSaving(true);

      // Update only the fields that can be changed
      const updateData = {
        first_name: profile.first_name.trim(),
        last_name: profile.last_name.trim()
      };

      // Validate required fields
      if (!updateData.first_name || !updateData.last_name) {
        showNotification('First name and last name are required', 'error');
        return;
      }

      // TODO: Implement profile update API call
      // await apiRequest('/user/profile', { method: 'PUT', data: updateData });
      
      console.log('Saving profile:', updateData);

      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));

      showNotification('Profile updated successfully', 'success');
    } catch (error) {
      console.error('Error saving profile:', error);
      showNotification('Failed to update profile', 'error');
    } finally {
      setSaving(false);
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role.toLowerCase()) {
      case 'admin':
        return 'bg-red-100 text-red-800';
      case 'member':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (profileLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-1">Profile Information</h3>
        <p className="text-sm text-gray-500">
          Update your personal information and view your account details.
        </p>
      </div>

      {/* Editable Profile Information */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center mb-4">
          <UserCircleIcon className="h-5 w-5 text-primary-600 mr-2" />
          <h4 className="text-base font-medium text-gray-900">Personal Details</h4>
        </div>
        
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              First Name *
            </label>
            <input
              type="text"
              value={profile.first_name}
              onChange={(e) => handleInputChange('first_name', e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              placeholder="Enter your first name"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Last Name *
            </label>
            <input
              type="text"
              value={profile.last_name}
              onChange={(e) => handleInputChange('last_name', e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              placeholder="Enter your last name"
              required
            />
          </div>
        </div>
      </div>

      {/* Read-only Account Information */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center mb-4">
          <BuildingOfficeIcon className="h-5 w-5 text-blue-600 mr-2" />
          <h4 className="text-base font-medium text-gray-900">Account Details</h4>
        </div>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between py-3 border-b border-gray-200">
            <div className="flex items-center">
              <EnvelopeIcon className="h-4 w-4 text-gray-400 mr-3" />
              <div>
                <div className="text-sm font-medium text-gray-900">Email Address</div>
                <div className="text-sm text-gray-500">Your login email</div>
              </div>
            </div>
            <div className="text-sm text-gray-900">{profile.email}</div>
          </div>

          <div className="flex items-center justify-between py-3 border-b border-gray-200">
            <div className="flex items-center">
              <ShieldCheckIcon className="h-4 w-4 text-gray-400 mr-3" />
              <div>
                <div className="text-sm font-medium text-gray-900">Role</div>
                <div className="text-sm text-gray-500">Your access level</div>
              </div>
            </div>
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${getRoleBadgeColor(profile.role)}`}>
              {profile.role}
            </span>
          </div>

          <div className="flex items-center justify-between py-3 border-b border-gray-200">
            <div className="flex items-center">
              <BuildingOfficeIcon className="h-4 w-4 text-gray-400 mr-3" />
              <div>
                <div className="text-sm font-medium text-gray-900">Organization</div>
                <div className="text-sm text-gray-500">Your company or team</div>
              </div>
            </div>
            <div className="text-sm text-gray-900">{profile.organization || 'Not set'}</div>
          </div>

          <div className="flex items-center justify-between py-3">
            <div className="flex items-center">
              <CalendarIcon className="h-4 w-4 text-gray-400 mr-3" />
              <div>
                <div className="text-sm font-medium text-gray-900">Member Since</div>
                <div className="text-sm text-gray-500">Account creation date</div>
              </div>
            </div>
            <div className="text-sm text-gray-900">{profile.joined_date}</div>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end pt-4 border-t border-gray-200">
        <button
          onClick={saveProfile}
          disabled={saving || !profile.first_name || !profile.last_name}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? (
            <>
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Saving...
            </>
          ) : (
            <>
              <CheckIcon className="h-4 w-4 mr-2" />
              Save Changes
            </>
          )}
        </button>
      </div>

      {/* Help Text */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex">
          <EnvelopeIcon className="h-5 w-5 text-blue-400 mr-2 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-blue-700">
            <p className="font-medium mb-1">Need to change your email or organization?</p>
            <p>Contact your administrator to update your email address, role, or organization settings.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfileSettingsImproved;